import { useState } from "react";
import { collection, addDoc, doc, getDoc } from "firebase/firestore";
import { auth, db } from "../firebase";
import { getCountryCode, detectCountryFromCity } from "../utils/flagEmoji";
import { formatDate } from "../utils/formatDate";
import { useMonetization } from "../context/MonetizationContext";
import { isMultiCityBlocked } from "../utils/entitlement";
import UpgradeModal from "../components/UpgradeModal";

const EMPTY_LEG = { city: "", country: "", countryCode: "", arrivalDate: "", departureDate: "", transportNote: "", arrivalStyle: "light", driveStyle: "morning" };

export default function CreateTrip({ onBack, onTripCreated, profile }) {
  const { premiumGateEnabled } = useMonetization();
  const [mode, setMode] = useState("single"); // "single" | "multi"
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [createError, setCreateError] = useState("");
  const [validationError, setValidationError] = useState("");
  const [detectedCode, setDetectedCode] = useState(null);
  const [showPaywall, setShowPaywall] = useState(false);

  // Single-city form
  const [form, setForm] = useState({
    name: "",
    destination: "",
    country: "",
    startDate: "",
    endDate: "",
    notes: "",
  });

  // Multi-city form
  const [mcForm, setMcForm] = useState({
    name: "",
    startDate: "",
    endDate: "",
    notes: "",
    startingPoint: "",
    legs: [{ ...EMPTY_LEG }, { ...EMPTY_LEG }],
  });

  const totalSteps = 2;

  const getDays = () => {
    if (!form.startDate || !form.endDate) return 0;
    const diff = new Date(form.endDate) - new Date(form.startDate);
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)) + 1);
  };

  const canNext = () => {
    if (mode === "single") {
      if (step === 1) return form.name.trim() && form.destination.trim();
      if (step === 2) return form.startDate && form.endDate && getDays() > 0;
      return true;
    }
    // multi-city
    if (step === 1) return mcForm.name.trim() && mcForm.startDate && mcForm.endDate && validateMcStep1() === "";
    if (step === 2) return validateMcLegs() === "";
    return true;
  };

  // ── Multi-city helpers ───────────────────────────────────────────────────
  const updateLeg = (idx, field, value) => {
    setMcForm((prev) => {
      const legs = prev.legs.map((l, i) => i === idx ? { ...l, [field]: value } : l);
      return { ...prev, legs };
    });
    setValidationError("");
  };

  const addLeg = () => {
    if (mcForm.legs.length >= 6) return;
    setMcForm((prev) => ({ ...prev, legs: [...prev.legs, { ...EMPTY_LEG }] }));
  };

  const removeLeg = (idx) => {
    if (mcForm.legs.length <= 2) return;
    setMcForm((prev) => ({ ...prev, legs: prev.legs.filter((_, i) => i !== idx) }));
  };

  const getMcDays = () => {
    if (!mcForm.startDate || !mcForm.endDate) return 0;
    const diff = new Date(mcForm.endDate) - new Date(mcForm.startDate);
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)) + 1);
  };

  const getLegDays = (leg) => {
    if (!leg.arrivalDate || !leg.departureDate) return 0;
    const diff = new Date(leg.departureDate) - new Date(leg.arrivalDate);
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)) + 1);
  };

  const validateMcStep1 = () => {
    if (!mcForm.name.trim()) return "Trip name is required";
    if (!mcForm.startDate || !mcForm.endDate) return "Start and end dates are required";
    if (new Date(mcForm.endDate) <= new Date(mcForm.startDate)) return "End date must be after start date";
    return "";
  };

  const validateMcLegs = () => {
    const start = new Date(mcForm.startDate);
    const end = new Date(mcForm.endDate);
    for (let i = 0; i < mcForm.legs.length; i++) {
      const leg = mcForm.legs[i];
      if (!leg.city.trim()) return `City is required for leg ${i + 1}`;
      const legCode = leg.countryCode || getCountryCode(leg.country);
      if (!legCode) return `We couldn't detect the country for ${leg.city}. Try adding the country name.`;
      if (!leg.arrivalDate || !leg.departureDate) return `Dates required for ${leg.city || `leg ${i + 1}`}`;
      const arr = new Date(leg.arrivalDate);
      const dep = new Date(leg.departureDate);
      if (dep < arr) return `Departure must be after arrival in ${leg.city}`;
      if (arr < start || dep > end) return `${leg.city} dates must be within the overall trip dates`;
    }
    // Check for overlaps
    const sorted = [...mcForm.legs].sort((a, b) => new Date(a.arrivalDate) - new Date(b.arrivalDate));
    for (let i = 0; i < sorted.length - 1; i++) {
      if (new Date(sorted[i].departureDate) > new Date(sorted[i + 1].arrivalDate)) {
        return `Dates overlap between ${sorted[i].city} and ${sorted[i + 1].city}`;
      }
    }
    return "";
  };

  // ── Save handlers ────────────────────────────────────────────────────────
  const handleCreate = async () => {
    if (!auth.currentUser) { setCreateError("You've been signed out. Please sign in again."); setSaving(false); return; }
    setSaving(true);
    setCreateError("");
    try {
      const userSnap = await getDoc(doc(db, "users", auth.currentUser.uid));
      if (!userSnap.exists()) {
        setCreateError("Your profile couldn't be loaded. Please try again.");
        setSaving(false);
        return;
      }
      const userData = userSnap.data();

      const tripData = {
        ...form,
        countryCode: detectedCode || getCountryCode(form.country) || null,
        createdBy: auth.currentUser.uid,
        createdByName: userData?.name || "Someone",
        createdAt: new Date(),
        status: "planning",
        days: getDays(),
        memberUids: [auth.currentUser.uid],
        itinerary: null,
        expenses: [],
      };

      const docRef = await addDoc(collection(db, "trips"), tripData);
      onTripCreated({ id: docRef.id, ...tripData });
    } catch (err) {
      console.error(err);
      setCreateError("Something went wrong. Please try again.");
    }
    setSaving(false);
  };

  const handleCreateMultiCity = async () => {
    if (!auth.currentUser) { setCreateError("You've been signed out. Please sign in again."); setSaving(false); return; }
    const legErr = validateMcLegs();
    if (legErr) { setCreateError(legErr); setSaving(false); return; }

    setSaving(true);
    setCreateError("");
    try {
      const userSnap = await getDoc(doc(db, "users", auth.currentUser.uid));
      if (!userSnap.exists()) {
        setCreateError("Your profile couldn't be loaded. Please try again.");
        setSaving(false);
        return;
      }
      const userData = userSnap.data();

      const enrichedLegs = mcForm.legs.map((leg) => ({
        ...leg,
        days: getLegDays(leg),
        countryCode: leg.countryCode || getCountryCode(leg.country) || null,
      }));

      const destinationStr = enrichedLegs.map((l) => l.city).join(", ");

      const tripData = {
        name: mcForm.name,
        isMultiCity: true,
        destination: destinationStr,
        startDate: mcForm.startDate,
        endDate: mcForm.endDate,
        days: getMcDays(),
        legs: enrichedLegs,
        notes: mcForm.notes,
        startingPoint: mcForm.startingPoint.trim() || null,
        countryCode: enrichedLegs[0]?.countryCode || null,
        memberUids: [auth.currentUser.uid],
        createdBy: auth.currentUser.uid,
        createdByName: userData?.name || "Someone",
        createdAt: new Date(),
        status: "planning",
        itinerary: null,
        expenses: [],
      };

      const docRef = await addDoc(collection(db, "trips"), tripData);
      onTripCreated({ id: docRef.id, ...tripData });
    } catch (err) {
      console.error(err);
      setCreateError("Something went wrong. Please try again.");
    }
    setSaving(false);
  };

  const handleNext = () => {
    if (mode === "single" && step === 1) {
      const code = detectedCode || getCountryCode(form.country);
      if (!code) {
        setValidationError("We couldn't detect this country. Try a major city name.");
        return;
      }
    }
    if (mode === "multi" && step === 1) {
      const err = validateMcStep1();
      if (err) { setValidationError(err); return; }
    }
    if (mode === "multi" && step === 2) {
      const err = validateMcLegs();
      if (err) { setValidationError(err); return; }
    }
    setValidationError("");
    setStep(step + 1);
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;1,400&family=DM+Sans:wght@300;400;500&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

        .ct-root {
          min-height: 100vh;
          background: #FAF6F0;
          font-family: 'DM Sans', sans-serif;
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 0 24px 80px;
        }

        .ct-top {
          width: 100%;
          max-width: 520px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 20px 0;
        }

        .ct-back {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 14px;
          font-weight: 500;
          color: rgba(26,23,20,0.55);
          cursor: pointer;
          background: none;
          border: none;
          font-family: 'DM Sans', sans-serif;
          transition: color 0.2s;
        }

        .ct-back:hover { color: #F5A623; }

        .ct-logo {
          font-family: 'Playfair Display', serif;
          font-size: 18px;
          font-weight: 600;
          color: #1E2D4A;
          letter-spacing: -0.02em;
        }

        .ct-logo span { color: #F5A623; }

        .ct-progress {
          width: 100%;
          max-width: 520px;
          margin-bottom: 28px;
        }

        .ct-progress-top {
          display: flex;
          justify-content: space-between;
          margin-bottom: 8px;
        }

        .ct-progress-label { font-size: 12px; color: rgba(26,23,20,0.45); }
        .ct-progress-step { font-size: 12px; font-weight: 500; color: #F5A623; }

        .ct-progress-bar-wrap {
          height: 4px;
          background: rgba(26,23,20,0.08);
          border-radius: 2px;
          overflow: hidden;
        }

        .ct-progress-bar {
          height: 4px;
          background: #F5A623;
          border-radius: 2px;
          transition: width 0.4s ease;
        }

        .ct-card {
          width: 100%;
          max-width: 520px;
          background: white;
          border-radius: 20px;
          border: 0.5px solid rgba(26,23,20,0.08);
          padding: 32px;
          box-shadow: 0 4px 32px rgba(26,23,20,0.06);
          animation: fadeUp 0.35s ease forwards;
        }

        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .ct-step-tag {
          display: inline-block;
          background: #FEF0DC;
          color: #C47A0A;
          font-size: 11px;
          font-weight: 500;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          padding: 4px 10px;
          border-radius: 100px;
          margin-bottom: 12px;
        }

        .ct-title {
          font-family: 'Playfair Display', serif;
          font-size: 24px;
          font-weight: 600;
          color: #1a1714;
          letter-spacing: -0.02em;
          margin-bottom: 6px;
          line-height: 1.2;
        }

        .ct-title em { font-style: italic; color: #F5A623; }

        .ct-sub {
          font-size: 14px;
          color: rgba(26,23,20,0.45);
          font-weight: 300;
          margin-bottom: 24px;
          line-height: 1.5;
        }

        .ct-field { margin-bottom: 14px; }

        .ct-label {
          display: block;
          font-size: 12px;
          font-weight: 500;
          color: rgba(26,23,20,0.55);
          margin-bottom: 6px;
        }

        .ct-input {
          width: 100%;
          padding: 13px 16px;
          border: 1.5px solid rgba(26,23,20,0.12);
          border-radius: 12px;
          background: #fafaf8;
          font-size: 14px;
          font-family: 'DM Sans', sans-serif;
          color: #1a1714;
          outline: none;
          transition: border-color 0.2s, box-shadow 0.2s;
        }

        .ct-input:focus {
          border-color: #F5A623;
          box-shadow: 0 0 0 3px rgba(245,166,35,0.1);
          background: white;
        }

        .ct-input::placeholder { color: rgba(26,23,20,0.3); }

        .ct-dates {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
          margin-bottom: 14px;
        }

        .ct-days-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          background: #FEF0DC;
          border-radius: 100px;
          padding: 6px 12px;
          font-size: 13px;
          font-weight: 500;
          color: #C47A0A;
          margin-bottom: 16px;
        }

        .ct-days-dot { width: 6px; height: 6px; border-radius: 50%; background: #F5A623; }

        .ct-btn-row { display: flex; gap: 10px; margin-top: 24px; }

        .ct-btn-back {
          padding: 13px 20px;
          border: 1.5px solid rgba(26,23,20,0.12);
          border-radius: 12px; background: white;
          font-size: 14px; font-weight: 500;
          color: rgba(26,23,20,0.55); cursor: pointer;
          font-family: 'DM Sans', sans-serif; transition: all 0.15s;
        }

        .ct-btn-back:hover { border-color: #F5A623; color: #C47A0A; }

        .ct-btn-next {
          flex: 1; padding: 13px;
          background: #F5A623; color: #1E2D4A;
          border: none; border-radius: 12px;
          font-size: 14px; font-weight: 500;
          font-family: 'DM Sans', sans-serif;
          cursor: pointer;
          transition: background 0.2s, transform 0.15s;
        }

        .ct-btn-next:hover:not(:disabled) { background: #C47A0A; color: white; transform: translateY(-1px); }
        .ct-btn-next:disabled { opacity: 0.4; cursor: not-allowed; transform: none; }

        .ct-textarea {
          width: 100%;
          padding: 13px 16px;
          border: 1.5px solid rgba(26,23,20,0.12);
          border-radius: 12px; background: #fafaf8;
          font-size: 14px; font-family: 'DM Sans', sans-serif;
          color: #1a1714; outline: none; resize: none; height: 80px;
          transition: border-color 0.2s;
        }

        .ct-textarea:focus {
          border-color: #F5A623;
          box-shadow: 0 0 0 3px rgba(245,166,35,0.1);
          background: white;
        }

        .ct-textarea::placeholder { color: rgba(26,23,20,0.3); }

        /* Mode toggle */
        .ct-mode-toggle {
          display: flex;
          gap: 0;
          background: rgba(26,23,20,0.06);
          border-radius: 100px;
          padding: 4px;
          margin-bottom: 28px;
        }

        .ct-mode-pill {
          flex: 1;
          padding: 10px 14px;
          border-radius: 100px;
          border: none;
          font-size: 13px;
          font-weight: 500;
          font-family: 'DM Sans', sans-serif;
          cursor: pointer;
          transition: all 0.2s;
          text-align: center;
          color: #1E2D4A;
          background: transparent;
        }

        .ct-mode-pill.active {
          background: #F5A623;
          color: #1E2D4A;
          box-shadow: 0 2px 8px rgba(245,166,35,0.3);
        }

        .ct-mode-pill:not(.active) {
          background: transparent;
          color: rgba(26,23,20,0.5);
        }

        .ct-premium-badge {
          font-size: 10px;
          background: #1E2D4A;
          color: #F5A623;
          padding: 2px 6px;
          border-radius: 100px;
          margin-left: 5px;
          font-weight: 600;
          letter-spacing: 0.03em;
          vertical-align: middle;
        }

        /* Multi-city leg cards */
        .ct-leg-card {
          border: 1.5px solid rgba(26,23,20,0.1);
          border-radius: 14px;
          padding: 16px;
          margin-bottom: 12px;
          background: #fafaf8;
          position: relative;
        }

        .ct-leg-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 12px;
        }

        .ct-leg-num {
          font-size: 11px;
          font-weight: 600;
          color: #F5A623;
          text-transform: uppercase;
          letter-spacing: 0.06em;
        }

        .ct-leg-remove {
          width: 26px; height: 26px;
          border-radius: 50%;
          background: white;
          border: 1.5px solid rgba(26,23,20,0.12);
          color: rgba(26,23,20,0.4);
          font-size: 14px;
          cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          transition: all 0.15s;
          line-height: 1;
          font-family: 'DM Sans', sans-serif;
        }

        .ct-leg-remove:hover { border-color: #c9622f; color: #c9622f; }
        .ct-leg-remove:disabled { opacity: 0.2; cursor: not-allowed; }

        .ct-add-leg-btn {
          width: 100%;
          padding: 12px;
          border: 1.5px dashed #F5A623;
          border-radius: 12px;
          background: transparent;
          font-size: 13px;
          font-weight: 500;
          color: #C47A0A;
          cursor: pointer;
          font-family: 'DM Sans', sans-serif;
          transition: all 0.15s;
          margin-bottom: 14px;
        }

        .ct-add-leg-btn:hover { background: #FEF0DC; }
        .ct-add-leg-btn:disabled { opacity: 0.35; cursor: not-allowed; }

        .ct-validation-error {
          margin-top: 12px;
          padding: 12px 14px;
          background: #fef0ed;
          border: 1px solid rgba(201,98,47,0.2);
          border-radius: 10px;
          font-size: 13px;
          color: #c9622f;
        }

        .ct-leg-days-badge {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          background: #FEF0DC;
          border-radius: 100px;
          padding: 3px 9px;
          font-size: 11px;
          font-weight: 500;
          color: #C47A0A;
          margin-top: 6px;
        }
      `}</style>

      <div className="ct-root">
        <div className="ct-top">
          <button className="ct-back" onClick={onBack}>← Back</button>
          <div className="ct-logo">Solis<span> ☀️</span></div>
          <div style={{width: 60}}></div>
        </div>

        <div className="ct-progress">
          <div className="ct-progress-top">
            <span className="ct-progress-label">Creating your trip</span>
            <span className="ct-progress-step">Step {step} of {totalSteps}</span>
          </div>
          <div className="ct-progress-bar-wrap">
            <div className="ct-progress-bar" style={{width: `${(step / totalSteps) * 100}%`}}></div>
          </div>
        </div>

        <div className="ct-card" key={`${mode}-${step}`}>

          {/* ── Mode toggle (step 1 only) ── */}
          {step === 1 && (
            <div className="ct-mode-toggle">
              <button
                className={`ct-mode-pill ${mode === "single" ? "active" : ""}`}
                onClick={() => { setMode("single"); setValidationError(""); }}
              >
                Single Destination
              </button>
              <button
                className={`ct-mode-pill ${mode === "multi" ? "active" : ""}`}
                onClick={() => {
                  if (isMultiCityBlocked({ premiumGateEnabled, profile })) {
                    setShowPaywall(true);
                  } else {
                    setMode("multi");
                    setValidationError("");
                  }
                }}
              >
                Multi-City
                {premiumGateEnabled && <span className="ct-premium-badge">✨ Premium</span>}
              </button>
            </div>
          )}

          {/* ════════════════ SINGLE-CITY STEPS ════════════════ */}
          {mode === "single" && (
            <>
              {step === 1 && (
                <>
                  <span className="ct-step-tag">The destination</span>
                  <h2 className="ct-title">Where are you <em>headed?</em></h2>
                  <p className="ct-sub">Give your trip a name and tell us where you're going.</p>

                  <div className="ct-field">
                    <label className="ct-label">Trip name</label>
                    <input className="ct-input" type="text" placeholder="e.g. Bali with the crew 🌴"
                      value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} />
                  </div>

                  <div className="ct-field">
                    <label className="ct-label">Destination city</label>
                    <input className="ct-input" type="text" placeholder="e.g. Bali"
                      value={form.destination}
                      onChange={(e) => setForm({...form, destination: e.target.value})}
                      onBlur={async (e) => { const code = await detectCountryFromCity(e.target.value); if (code) setDetectedCode(code); }} />
                  </div>

                  <div className="ct-field">
                    <label className="ct-label">Country</label>
                    <input className="ct-input" type="text" placeholder="e.g. Indonesia"
                      value={form.country} onChange={(e) => { setForm({...form, country: e.target.value}); setValidationError(""); }} />
                  </div>
                  {validationError && <div className="ct-validation-error">{validationError}</div>}
                </>
              )}

              {step === 2 && (
                <>
                  <span className="ct-step-tag">The dates</span>
                  <h2 className="ct-title">When are you <em>going?</em></h2>
                  <p className="ct-sub">Set your travel dates so the AI can plan your day-by-day itinerary.</p>

                  <div className="ct-dates">
                    <div className="ct-field" style={{marginBottom: 0}}>
                      <label className="ct-label">Departure date</label>
                      <input className="ct-input" type="date"
                        value={form.startDate}
                        min={new Date().toISOString().split("T")[0]}
                        onChange={(e) => setForm({...form, startDate: e.target.value})} />
                    </div>
                    <div className="ct-field" style={{marginBottom: 0}}>
                      <label className="ct-label">Return date</label>
                      <input className="ct-input" type="date"
                        value={form.endDate}
                        min={form.startDate || new Date().toISOString().split("T")[0]}
                        onChange={(e) => setForm({...form, endDate: e.target.value})} />
                    </div>
                  </div>

                  {getDays() > 0 && (
                    <div className="ct-days-badge">
                      <div className="ct-days-dot"></div>
                      {getDays()} day{getDays() !== 1 ? "s" : ""} · {form.destination}
                    </div>
                  )}

                  <div className="ct-field">
                    <label className="ct-label">Trip notes <span style={{color:'rgba(26,23,20,0.35)',fontWeight:300}}>— optional</span></label>
                    <textarea className="ct-textarea"
                      placeholder="e.g. We want a mix of beaches and culture. Jamie is vegetarian..."
                      value={form.notes}
                      onChange={(e) => setForm({...form, notes: e.target.value})} />
                  </div>
                </>
              )}
            </>
          )}

          {/* ════════════════ MULTI-CITY STEPS ════════════════ */}
          {mode === "multi" && (
            <>
              {step === 1 && (
                <>
                  <span className="ct-step-tag">The trip</span>
                  <h2 className="ct-title">Plan your <em>multi-city</em> adventure</h2>
                  <p className="ct-sub">Name your trip and set the overall travel window.</p>

                  <div className="ct-field">
                    <label className="ct-label">Trip name</label>
                    <input className="ct-input" type="text" placeholder="e.g. Europe Summer 2026"
                      value={mcForm.name} onChange={(e) => setMcForm({...mcForm, name: e.target.value})} />
                  </div>

                  <div className="ct-dates">
                    <div className="ct-field" style={{marginBottom: 0}}>
                      <label className="ct-label">Overall start date</label>
                      <input className="ct-input" type="date"
                        value={mcForm.startDate}
                        min={new Date().toISOString().split("T")[0]}
                        onChange={(e) => setMcForm({...mcForm, startDate: e.target.value})} />
                    </div>
                    <div className="ct-field" style={{marginBottom: 0}}>
                      <label className="ct-label">Overall end date</label>
                      <input className="ct-input" type="date"
                        value={mcForm.endDate}
                        min={mcForm.startDate || new Date().toISOString().split("T")[0]}
                        onChange={(e) => setMcForm({...mcForm, endDate: e.target.value})} />
                    </div>
                  </div>

                  {getMcDays() > 0 && (
                    <div className="ct-days-badge">
                      <div className="ct-days-dot"></div>
                      {getMcDays()} days total
                    </div>
                  )}

                  <div className="ct-field">
                    <label className="ct-label">Trip notes <span style={{color:'rgba(26,23,20,0.35)',fontWeight:300}}>— optional</span></label>
                    <textarea className="ct-textarea"
                      placeholder="e.g. Mix of culture and beaches. Group of 4 with one vegetarian..."
                      value={mcForm.notes}
                      onChange={(e) => setMcForm({...mcForm, notes: e.target.value})} />
                  </div>

                  {validationError && <div className="ct-validation-error">{validationError}</div>}
                </>
              )}

              {step === 2 && (
                <>
                  <span className="ct-step-tag">The cities</span>
                  <h2 className="ct-title">Add your <em>destinations</em></h2>
                  <p className="ct-sub">Add between 2 and 6 cities. Dates must fall within {formatDate(mcForm.startDate)} → {formatDate(mcForm.endDate)}.</p>

                  <div className="ct-field" style={{marginBottom: 16}}>
                    <label className="ct-label">Starting point <span style={{color:'rgba(26,23,20,0.35)',fontWeight:300}}>— optional</span></label>
                    <input className="ct-input" type="text" placeholder="e.g. New York (your home city)"
                      value={mcForm.startingPoint}
                      onChange={(e) => setMcForm({...mcForm, startingPoint: e.target.value})} />
                  </div>

                  {mcForm.legs.map((leg, idx) => (
                    <div key={idx} className="ct-leg-card">
                      <div className="ct-leg-header">
                        <span className="ct-leg-num">City {idx + 1}</span>
                        <button
                          className="ct-leg-remove"
                          onClick={() => removeLeg(idx)}
                          disabled={mcForm.legs.length <= 2}
                          title="Remove city"
                        >×</button>
                      </div>

                      <div className="ct-field" style={{marginBottom: 10}}>
                        <label className="ct-label">City / destination</label>
                        <input className="ct-input" type="text" placeholder="e.g. Paris"
                          value={leg.city}
                          onChange={(e) => updateLeg(idx, "city", e.target.value)}
                          onBlur={async (e) => { const code = await detectCountryFromCity(e.target.value); if (code) updateLeg(idx, 'countryCode', code); }} />
                      </div>

                      <div className="ct-field" style={{marginBottom: 10}}>
                        <label className="ct-label">Country</label>
                        <input className="ct-input" type="text" placeholder="e.g. France"
                          value={leg.country}
                          onChange={(e) => updateLeg(idx, "country", e.target.value)} />
                      </div>

                      <div className="ct-dates" style={{marginBottom: 8}}>
                        <div>
                          <label className="ct-label">Arrival date</label>
                          <input className="ct-input" type="date"
                            value={leg.arrivalDate}
                            min={mcForm.startDate}
                            max={mcForm.endDate}
                            onChange={(e) => updateLeg(idx, "arrivalDate", e.target.value)} />
                        </div>
                        <div>
                          <label className="ct-label">Departure date</label>
                          <input className="ct-input" type="date"
                            value={leg.departureDate}
                            min={leg.arrivalDate || mcForm.startDate}
                            max={mcForm.endDate}
                            onChange={(e) => updateLeg(idx, "departureDate", e.target.value)} />
                        </div>
                      </div>

                      {leg.arrivalDate && leg.departureDate && getLegDays(leg) > 0 && (
                        <p style={{fontSize:12, color:'#9CA3AF', marginTop:4}}>
                          {getLegDays(leg)} day{getLegDays(leg) !== 1 ? "s" : ""} in {leg.city || "this city"}
                        </p>
                      )}

                      <div className="ct-field" style={{marginBottom: 10, marginTop: 10}}>
                        <label className="ct-label">Transport note <span style={{color:'rgba(26,23,20,0.35)',fontWeight:300}}>— optional</span></label>
                        <input className="ct-input" type="text" placeholder="e.g. Flight BA123 from London"
                          value={leg.transportNote}
                          onChange={(e) => updateLeg(idx, "transportNote", e.target.value)} />
                      </div>

                      <div className="ct-field" style={{marginBottom: 10}}>
                        <label className="ct-label">Arrival Day Style</label>
                        <div style={{display:'flex',gap:8,flexWrap:'wrap',marginTop:4}}>
                          {[
                            {value:'light',  label:'🌅 Settle in + light activities'},
                            {value:'settle', label:'😴 Settle in only'},
                          ].map(opt => (
                            <button key={opt.value} type="button"
                              onClick={() => updateLeg(idx, 'arrivalStyle', opt.value)}
                              style={{padding:'6px 14px',borderRadius:20,border:'none',cursor:'pointer',fontSize:12,fontFamily:'DM Sans,sans-serif',fontWeight:500,
                                background: leg.arrivalStyle === opt.value ? '#F5A623' : '#F0EDE8',
                                color:      leg.arrivalStyle === opt.value ? 'white'   : '#1E2D4A',
                              }}>
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="ct-field" style={{marginBottom: 0}}>
                        <label className="ct-label">Drive Style</label>
                        <div style={{display:'flex',gap:8,flexWrap:'wrap',marginTop:4}}>
                          {[
                            {value:'morning',   label:'🌄 Morning Driver'},
                            {value:'afternoon', label:'🌆 Afternoon Driver'},
                            {value:'overnight', label:'🌙 Overnight Driver'},
                          ].map(opt => (
                            <button key={opt.value} type="button"
                              onClick={() => updateLeg(idx, 'driveStyle', opt.value)}
                              style={{padding:'6px 14px',borderRadius:20,border:'none',cursor:'pointer',fontSize:12,fontFamily:'DM Sans,sans-serif',fontWeight:500,
                                background: leg.driveStyle === opt.value ? '#F5A623' : '#F0EDE8',
                                color:      leg.driveStyle === opt.value ? 'white'   : '#1E2D4A',
                              }}>
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}

                  {mcForm.legs.some(l => l.city) && (
                    <p style={{fontSize:13, color:'#F5A623', fontStyle:'italic', marginBottom:12, textAlign:'center'}}>
                      {[mcForm.startingPoint || 'Home', ...mcForm.legs.map(l => l.city).filter(Boolean), mcForm.startingPoint || 'Home'].join(' → ')}
                    </p>
                  )}

                  <button
                    className="ct-add-leg-btn"
                    onClick={addLeg}
                    disabled={mcForm.legs.length >= 6}
                  >
                    + Add Another City {mcForm.legs.length >= 6 ? "(max 6)" : ""}
                  </button>

                  {validationError && <div className="ct-validation-error">{validationError}</div>}
                </>
              )}
            </>
          )}

          {step === totalSteps && (
            <div style={{
              background: '#FEF0DC', borderRadius: 12,
              padding: '12px 14px', display: 'flex',
              gap: 8, alignItems: 'flex-start', marginTop: 8, marginBottom: 4
            }}>
              <div style={{width:6,height:6,borderRadius:'50%',background:'#F5A623',flexShrink:0,marginTop:5}}></div>
              <p style={{fontSize:12,color:'#C47A0A',lineHeight:1.5}}>
                You can invite others by sharing the trip's invite link from the dashboard.
              </p>
            </div>
          )}

          <div className="ct-btn-row">
            {step > 1 && (
              <button className="ct-btn-back" onClick={() => { setStep(step - 1); setValidationError(""); }}>← Back</button>
            )}
            {step < totalSteps ? (
              <button className="ct-btn-next" disabled={!canNext()} onClick={handleNext}>
                Continue →
              </button>
            ) : (
              <button className="ct-btn-next" disabled={saving} onClick={mode === "single" ? handleCreate : handleCreateMultiCity}>
                {saving ? "Creating trip..." : "Create trip 🌍"}
              </button>
            )}
          </div>

          {createError && (
            <div style={{marginTop:12,padding:'12px 14px',background:'#fef0ed',border:'1px solid rgba(201,98,47,0.2)',borderRadius:10,fontSize:13,color:'#c9622f'}}>
              {createError}
            </div>
          )}

        </div>
      </div>
      <UpgradeModal open={showPaywall} onClose={() => setShowPaywall(false)} />
    </>
  );
}
